import { autoUpdate, flip, offset, shift, useDismiss, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { useContext, useMemo, useState } from "react";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { floatingBoundaryMiddleware, MITFloatingPortal, useFloatingPortalContext } from "src/componentsUtils/PortalHelpers";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { GlassButton } from "src/components/GlassButtons";
import { AnnotationContextType } from "./ANNTypes";
import { downloadOverlayImage } from "./ANNImageOverlayStorage";
import { ExportData } from "src/managers/ExportData";
import { useAnnotations } from "./ANNState";
import { findServitutDocumentAnnotation } from "./ANNServitutDocumentAnnotations";

/**
 * Shows annotation count for a servitut tingbog row (same documentId as servitut details / ANN table).
 * Renders nothing while loading, on error, when the module is off, or when count is 0.
 */
export function ServitutAnnotationCountBadge(props: {
   documentId: string;
   datoloebenr?: string;
   downloadFileName?: string;
}) {
   const { documentId } = props;
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { state: sessionState } = useContext(SessionContext);
   const { root } = useFloatingPortalContext();
   const [tooltipOpen, setTooltipOpen] = useState(false);
   const organizationId = sessionState?.customerRef;
   const moduleOn = hasAccessToFeature(Feature.AnnotationModule);
   const { annotations, loading } = useAnnotations({
      kind: "scoped",
      contextType: AnnotationContextType.Servitut,
      identifier: documentId,
      organizationId,
   });
   const count = moduleOn ? annotations.length : 0;
   const { refs, floatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      placement: "top",
      open: tooltipOpen,
      middleware: [
         offset(5),
         ...(root != null ? floatingBoundaryMiddleware(root) : [flip({ padding: 8 }), shift({ padding: 8 })]),
      ],
      onOpenChange: setTooltipOpen,
   });
   const {
      getReferenceProps,
      getFloatingProps,
   } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);

   if (!documentId || !moduleOn) {
      return null;
   }
   if (loading || count < 1) {
      return null;
   }

   const locale = Localization.getLocale().toLowerCase();
   const isDanish = locale.startsWith("da");
   const annotationLabel = count === 1
      ? (isDanish ? "annotation" : "annotation")
      : (isDanish ? "annotationer" : "annotations");
   const title = `${count} ${annotationLabel}`;
   return (
      <>
         <span
            ref={refs.setReference}
            className="tingbog-servitut-annotation-count"
            aria-label={title}
            {...getReferenceProps()}
         >
            {count}
         </span>
         {tooltipOpen ? (
            <MITFloatingPortal>
               <div
                  ref={refs.setFloating}
                  className="GlassButtonToolTip"
                  style={floatingStyles}
                  {...getFloatingProps()}
               >
                  {title}
               </div>
            </MITFloatingPortal>
         ) : null}
      </>
   );
}

export function ServitutAnnotationQuickDownloadButton(props: {
   documentId: string;
   datoloebenr?: string;
   downloadFileName?: string;
}) {
   const { documentId, datoloebenr, downloadFileName } = props;
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { state: sessionState } = useContext(SessionContext);
   const [downloading, setDownloading] = useState(false);
   const organizationId = sessionState?.customerRef;
   const moduleOn = hasAccessToFeature(Feature.AnnotationModule);
   const { annotations } = useAnnotations({
      kind: "scoped",
      contextType: AnnotationContextType.Servitut,
      identifier: documentId,
      organizationId,
   });
   const quickDownloadRecord = useMemo(
      () => moduleOn ? findServitutDocumentAnnotation(annotations, datoloebenr) : null,
      [annotations, datoloebenr, moduleOn]
   );

   async function handleQuickDownload() {
      if (!quickDownloadRecord?.document?.s3Key || downloading) {
         return;
      }
      setDownloading(true);
      try {
         const { blob, fileName } = await downloadOverlayImage(quickDownloadRecord.document.s3Key);
         const resolvedDownloadName =
            downloadFileName ||
            quickDownloadRecord.document.filename ||
            fileName ||
            `document-${quickDownloadRecord.id}.pdf`;
         ExportData.downloadBlob(resolvedDownloadName, blob);
      } finally {
         setDownloading(false);
      }
   }

   if (!quickDownloadRecord) {
      return null;
   }

   const locale = Localization.getLocale().toLowerCase();
   const quickDownloadTitle = locale.startsWith("da")
      ? "Download servituttekst tidligere udklippet som annotation"
      : "Download servitut text previously extracted as annotation";

   return (
      <GlassButton
         className="tingbog-download-button"
         disabled={downloading}
         onClick={handleQuickDownload}
         title={downloading ? Localization.getText("Downloading...") : quickDownloadTitle}
         title-placement="top"
      >
         <MIBIcon icon="download" />
      </GlassButton>
   );
}
